import { BuyInvoiceApi } from "./BuyInvoiceApi";
import { ProviderInvoiceApi } from "./ProviderInvoiceApi";
import { ProviderRecibeApi, ProviderRecibeDataType } from "./ProviderRecibeApi";
import {
  buildRepairPlan,
  isRepairCandidate,
  parseMotivoReferences,
  RepairBatchSummary,
  RepairPlan,
} from "@utils/repairProviderRecibeLinks";

async function buildPlanForRecibe(recibe: ProviderRecibeDataType): Promise<RepairPlan> {
  const recibeId = `${recibe.id}`;
  const parsed = parseMotivoReferences(recibe.motivo);

  const [{ items: linkedBuyInvoices }, linkedProviderInvoices] = await Promise.all([
    BuyInvoiceApi.fetchAllByRecibe(recibeId),
    ProviderInvoiceApi.getInvoiceByRecibeId(recibeId),
  ]);

  const buyInvoicesByNumero = new Map<number, Awaited<ReturnType<typeof BuyInvoiceApi.findByNumero>>>();
  const providerInvoicesByNumero = new Map<string, Awaited<ReturnType<typeof ProviderInvoiceApi.findByNumero>>>();

  await Promise.all([
    ...parsed.fecNumeros.map(async (numero) => {
      const invoice = await BuyInvoiceApi.findByNumero(numero);
      if (invoice) {
        buyInvoicesByNumero.set(numero, invoice);
      }
    }),
    ...parsed.providerInvoiceNumeros.map(async (numero) => {
      const invoice = await ProviderInvoiceApi.findByNumero(numero);
      if (invoice) {
        providerInvoicesByNumero.set(numero, invoice);
      }
    }),
  ]);

  const providerNamesById = new Map<string, string>();
  for (const invoice of [...buyInvoicesByNumero.values(), ...providerInvoicesByNumero.values()]) {
    if (invoice?.providerId && "provider" in invoice && invoice.provider?.nombre) {
      providerNamesById.set(`${invoice.providerId}`, `${invoice.provider.nombre}`);
    }
  }

  if (recibe.providerId && recibe.provider?.nombre) {
    providerNamesById.set(`${recibe.providerId}`, `${recibe.provider.nombre}`);
  }

  return buildRepairPlan({
    recibe,
    parsed,
    buyInvoicesByNumero: buyInvoicesByNumero as Map<number, NonNullable<Awaited<ReturnType<typeof BuyInvoiceApi.findByNumero>>>>,
    providerInvoicesByNumero: providerInvoicesByNumero as Map<string, NonNullable<Awaited<ReturnType<typeof ProviderInvoiceApi.findByNumero>>>>,
    linkedBuyInvoices,
    linkedProviderInvoices,
    providerNamesById,
  });
}

async function scanRepairCandidates(): Promise<RepairPlan[]> {
  const { items: recibos } = await ProviderRecibeApi.fetchAll({}, undefined, 5000);
  const plans: RepairPlan[] = [];

  for (const recibe of recibos) {
    if (recibe.anulado) {
      continue;
    }

    const parsed = parseMotivoReferences(recibe.motivo);
    const recibeId = `${recibe.id}`;
    const [{ items: linkedBuyInvoices }, linkedProviderInvoices] = await Promise.all([
      BuyInvoiceApi.fetchAllByRecibe(recibeId),
      ProviderInvoiceApi.getInvoiceByRecibeId(recibeId),
    ]);

    if (!isRepairCandidate(recibe, parsed, linkedBuyInvoices.length, linkedProviderInvoices.length)) {
      continue;
    }

    plans.push(await buildPlanForRecibe(recibe));
  }

  return plans;
}

async function repairProviderRecibe(recibeId: string): Promise<RepairPlan> {
  const recibe = await ProviderRecibeApi.get(recibeId, {
    selectionSet: ["id", "numero_recibo", "motivo", "providerId", "anulado", "provider.*"],
  });

  const plan = await buildPlanForRecibe(recibe);
  if (plan.status !== "ready") {
    return plan;
  }

  const providerId = `${plan.proposedProviderId}`;

  if (!recibe.providerId) {
    await ProviderRecibeApi.update(recibeId, { providerId });
  }

  await Promise.all(
    plan.documents
      .filter((document) => !document.alreadyLinked)
      .map((document) => {
        if (document.kind === "buyInvoice") {
          return BuyInvoiceApi.update(document.id, {
            id: document.id,
            providerRecibeId: recibeId,
            pagada: true,
            status: "pagada",
            providerId,
            buyInvoiceType: "Buy",
          });
        }

        return ProviderInvoiceApi.update(document.id, {
          id: document.id,
          providerRecibeId: recibeId,
          pagada: true,
          status: "pagada",
          providerId,
        });
      }),
  );

  return {
    ...plan,
    status: "skipped",
    message: "Recibo reparado correctamente",
  };
}

async function repairProviderRecibes(recibeIds: string[]): Promise<RepairBatchSummary> {
  const summary: RepairBatchSummary = {
    repaired: [],
    skipped: [],
    failed: [],
  };

  for (const recibeId of recibeIds) {
    try {
      const recibe = await ProviderRecibeApi.get(recibeId, {
        selectionSet: ["id", "numero_recibo", "motivo", "providerId", "anulado"],
      });
      const previewPlan = await buildPlanForRecibe(recibe);

      if (previewPlan.status === "ready") {
        await repairProviderRecibe(recibeId);
        summary.repaired.push(recibeId);
        continue;
      }

      if (previewPlan.status === "skipped") {
        summary.skipped.push(recibeId);
        continue;
      }

      summary.failed.push({
        recibeId,
        message: previewPlan.message,
      });
    } catch (err) {
      summary.failed.push({
        recibeId,
        message: err instanceof Error ? err.message : "Error desconocido",
      });
    }
  }

  return summary;
}

export const ProviderRecibeRepairApi = {
  scanRepairCandidates,
  buildPlanForRecibe,
  repairProviderRecibe,
  repairProviderRecibes,
};
